import jsPDF from "jspdf";
import { PTSCONVERSION, imageUrlToDataUrl } from "../../../../utils";

// Logo
import logo from "/public/images/studio-logo-green.png";
import { id } from "date-fns/locale";

interface FooterProps {
  output: boolean;
  margin?: number;
  textFillColor: string;
  startX?: number;
  imageHeight?: number;
  marketDetails: {
    logo?: string;
    name?: string;
    email?: string;
    phone?: string;
  };
}

export default async function footer(doc: jsPDF, config: FooterProps) {
  const pageCount = doc.getNumberOfPages();
  const output = config.output;
  const margin = config.margin || 10;
  let currentX = margin;

  // Add Studio Logo
  currentX = await martEyeLogo(doc, {
    ...config,
    startX: currentX,
    output: false,
  });

  // Add Market Logo
  currentX = await marketLogo(doc, config.marketDetails.logo || "", {
    ...config,
    startX: currentX,
    output: false,
  });

  // Want to calculate the height of the legal text
  const legalHeight = marketText(doc, {
    ...config,
    output: false,
    startX: currentX,
  });

  footerLineBreak(
    doc,
    {
      ...config,
      output: false,
    },
    legalHeight
  );

  if (!output) {
    return;
  }

  for (var i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    currentX = margin;

    pageNumbers(doc, config, legalHeight);

    // Add Studio Logo
    currentX = await martEyeLogo(doc, {
      ...config,
      startX: currentX,
    });

    // Add Market Logo
    currentX = await marketLogo(doc, config.marketDetails.logo || "", {
      ...config,
      startX: currentX,
    });

    marketText(doc, {
      ...config,
      startX: currentX,
    });

    footerLineBreak(doc, config, legalHeight);
  }
}

async function martEyeLogo(doc: jsPDF, config: FooterProps) {
  const margin = config.margin || 10;
  const startX = config.startX || margin;
  const output = config.output;
  const imageHeight = config.imageHeight || 6;
  const pageHeight = doc.internal.pageSize.getHeight();

  // Get the martEye logo
  const { src, width, height } = logo;

  // Want to scale the image to be a certain height
  const scale = imageHeight / height;

  const scaledWidth = width * scale;

  // Add the image to the document
  if (output) {
    // Get the base 64 image
    let image = new Image();
    image.src = src;

    doc.addImage(
      image,
      "PNG",
      startX,
      pageHeight - imageHeight - margin / 4 - 1.5,
      scaledWidth,
      imageHeight,
      undefined,
      "FAST"
    );
  }

  // Add 25% margin to the width
  return scaledWidth + margin * 1.5;
}
async function marketLogo(doc: jsPDF, src: string, config: FooterProps) {
  const margin = config.margin || 10;
  let startX = config.startX || margin;
  const output = config.output || true;
  const imageHeight = config.imageHeight || 8;
  const pageHeight = doc.internal.pageSize.getHeight();

  if (!src) {
    return startX;
  }

  // Clean the src if it has a ? in it
  src = src.split("?")[0];

  // Get the image dimensions
  const { width, height, format } = await getImageDimensions(src);

  // Want to scale the image to be a certain height
  const scale = imageHeight / height;

  let scaledWidth = width * scale;
  if (width === height) {
    scaledWidth = imageHeight;
  }

  if (output) {
    let linePosition = doc.linePosition || pageHeight - margin / 2;

    // Also add a vertical line to separate the logo from the content
    doc.setLineWidth(0.1);
    doc.setDrawColor("#8B8B8B");
    doc.line(startX, linePosition + 2, startX, pageHeight - 2);

    startX += margin / 2;

    // Add the image to the document
    // TODO change the way this is done
    const img = new Image();
    img.src = `${window.location.origin}/_next/image?w=96&q=75&url=${src}`;

    doc.addImage(
      img,
      "PNG",
      startX,
      pageHeight - imageHeight - margin / 4,
      scaledWidth,
      imageHeight
    );
  }

  // Add 25% margin to the width
  return startX + scaledWidth + margin / 4;
}

function marketText(doc: jsPDF, config: FooterProps) {
  // Config
  const margin = config.margin || 10;
  let startX = config.startX || margin;
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageNumberWidth = doc.getTextWidth("Page 100 of 1000");
  const output = config.output || true;

  let marketDetailsHeight = 0;

  // Set the total tags read and total tags to the page
  const marketName = config.marketDetails?.name || null;
  let spaceAvailable = pageWidth - pageNumberWidth - startX - margin * 2;

  if (marketName) {
    doc.setFontSize(8);
    doc.setFont("Inter", "bold");
    doc.setTextColor(config.textFillColor);
    marketDetailsHeight = doc.getLineHeight() * PTSCONVERSION;

    let expectedWidth = doc.getTextWidth(marketName);
    if (expectedWidth < spaceAvailable) {
      // Subtract the width from the space available
      spaceAvailable = spaceAvailable - expectedWidth;

      if (output) {
        doc.text(
          marketName,
          startX,
          pageHeight - margin / 2 - marketDetailsHeight,
          {
            align: "left",
            baseline: "top",
          }
        );
      }

      startX += expectedWidth + margin / 4;
    }
  }

  const marketEmail = config.marketDetails?.email || null;
  const marketPhone = config.marketDetails?.phone || null;

  let marketContact = "";
  if (marketEmail && marketPhone) {
    marketContact = `|  ${marketEmail}  |  ${marketPhone}`;
  } else if (marketEmail) {
    marketContact = `|  ${marketEmail}`;
  } else if (marketPhone) {
    marketContact = `|  ${marketPhone}`;
  }

  if (marketContact) {
    doc.setFont("Inter", "normal");
    marketDetailsHeight = doc.getLineHeight() * PTSCONVERSION;

    let expectedWidth = doc.getTextWidth(marketContact);
    if (expectedWidth < spaceAvailable) {
      // Subtract the width from the space available
      spaceAvailable = spaceAvailable - expectedWidth;

      if (output) {
        doc.text(
          marketContact,
          startX,
          pageHeight - margin / 2 - marketDetailsHeight,
          {
            align: "left",
            baseline: "top",
          }
        );
      }
      startX += expectedWidth;
    }
  }

  return marketDetailsHeight;
}

function pageNumbers(doc: jsPDF, config: FooterProps, legalTextHeight: number) {
  const margin = config.margin || 10;
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageCount = doc.getNumberOfPages();
  const currentPage = doc.getCurrentPageInfo().pageNumber;
  // Set Page Number to the current page
  doc.setFont("Inter", "normal");
  doc.setFontSize(8);
  doc.text(
    "Page " + String(currentPage) + " of " + String(pageCount),
    pageWidth - margin,
    pageHeight - margin / 2 - legalTextHeight,
    {
      align: "right",
      baseline: "top",
      maxWidth: pageWidth - margin * 2,
    }
  );
}

function footerLineBreak(
  doc: jsPDF,
  config: FooterProps,
  legalTextHeight: number
) {
  const margin = config.margin || 10;
  // If we are not outputting the document, then we don't need to add the line
  const linePosition =
    doc.internal.pageSize.getHeight() - margin / 2 - 5 - legalTextHeight;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const output = config.output || true;

  // Now safe zone for the bottom of each page is the line position - 5
  const safeZone = linePosition - 5;
  doc.mainFooterPosition = pageHeight - safeZone;

  doc.linePosition = linePosition;

  if (!output) {
    return safeZone;
  }

  // Now add a line to separate the footer from the content
  doc.setLineWidth(0.25);
  doc.setDrawColor(config.textFillColor);
  doc.line(margin, linePosition, pageWidth - margin, linePosition);
}

function getImageDimensions(url: string): Promise<{
  width: number;
  height: number;
  format: string;
}> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    // When the image is loaded, resolve the promise with its dimensions.
    img.onload = function () {
      // Get the format of the image
      const format = url.split(".").pop() ?? "png";

      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
        format: format,
      });
    };

    // If there's an error loading the image, reject the promise.
    img.onerror = function (e) {
      console.log("Error loading image", e);
      reject(new Error("Failed to load image at " + url));
    };

    // Set the image source.
    img.src = url;
  });
}
